import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useCouponHandler } from '@/hooks/useCouponHandler';
import { usePaymentHandler } from '@/hooks/usePaymentHandler';
import { calculateOriginalAmount } from '@/utils/payment/paymentUtils';
import { PaymentContextType, PackageType } from '@/types/payment';

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useCentralizedAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [discount, setDiscount] = useState(0);
  const [packageType, setPackageType] = useState<PackageType>(null);
  const [finalAmount, setFinalAmount] = useState(0);
  const [paymentType, setPaymentType] = useState<'order' | 'booking'>('order');
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [razorpayCheckCount, setRazorpayCheckCount] = useState(0);

  useEffect(() => {
    if (window.Razorpay) {
      console.log('PaymentContext: Razorpay already available');
      setRazorpayReady(true);
      return;
    }
    
    const handleRazorpayLoaded = () => {
      console.log('PaymentContext: Razorpay loaded event received');
      setRazorpayReady(true);
    };
    
    window.addEventListener('razorpay_loaded', handleRazorpayLoaded);
    
    const checkInterval = setInterval(() => {
      setRazorpayCheckCount(prev => prev + 1);
      
      if (window.Razorpay) {
        console.log('PaymentContext: Razorpay detected through interval check');
        setRazorpayReady(true);
        clearInterval(checkInterval);
      } else {
        console.log(`PaymentContext: Razorpay check #${razorpayCheckCount + 1} - not found`);
        
        if (razorpayCheckCount > 10) {
          clearInterval(checkInterval);
        }
      }
    }, Math.min(1000 + (razorpayCheckCount * 500), 5000));
    
    return () => {
      window.removeEventListener('razorpay_loaded', handleRazorpayLoaded);
      clearInterval(checkInterval);
    };
  }, [razorpayCheckCount]);

  useEffect(() => {
    try {
      console.log('PaymentContext: Initializing from URL params, user:', user?.id);
      const params = new URLSearchParams(location.search);
      const packageParam = params.get('package') as PackageType;
      const type = params.get('type') || 'order';
      
      if (type === 'booking') {
        console.log('PaymentContext: Processing a booking payment');
        setPaymentType('booking');
        
        if (!packageParam) {
          setPackageType('silver');
        } else {
          setPackageType(packageParam);
        }
        
        return;
      }
      
      if (!packageParam || (packageParam !== 'silver' && packageParam !== 'gold')) {
        console.log('PaymentContext: Invalid package type, redirecting to home');
        navigate('/');
        toast.error('Please select a package first');
        return;
      }

      setPackageType(packageParam);
      setPaymentType('order');
      const amount = calculateOriginalAmount(packageParam);
      setFinalAmount(amount);
      console.log(`PaymentContext: Package initialized: ${packageParam}, amount: ${amount}, type: ${type}`);
      
    } catch (error) {
      console.error('PaymentContext: Error initializing package:', error);
      navigate('/');
      toast.error('Error loading package details');
    }
  }, [location, navigate, user]);

  const {
    couponCode,
    setCouponCode,
    isApplyingCoupon,
    handleApplyCoupon,
    getOriginalAmount,
  } = useCouponHandler({
    packageType,
    userId: user?.id,
    setDiscount,
    setFinalAmount,
    couponType: paymentType === 'booking' ? 'Booking' : 'Order',
  });

  const {
    isProcessing,
    setIsProcessing,
    handleProceedToPayment,
  } = usePaymentHandler({
    userId: user?.id,
    userProfile: user,
    packageType,
    finalAmount,
    couponCode,
    discount,
    paymentType,
    razorpayReady: razorpayReady || Boolean(window.Razorpay),
  });

  const value: PaymentContextType = {
    packageType,
    couponCode,
    setCouponCode,
    isProcessing,
    setIsProcessing,
    discount,
    finalAmount,
    isApplyingCoupon,
    handleApplyCoupon,
    handleProceedToPayment,
    getOriginalAmount,
    paymentType,
    razorpayReady: razorpayReady || Boolean(window.Razorpay)
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
